import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EventDescriptionEditor } from "./EventDescriptionEditor";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUploadCropper } from "@/components/ui/image-upload-cropper";
import { useCreateEvent } from "@/queries/hooks";
import { EVENT_TYPE_LABELS } from "@/lib/event-constants";
import { EventType } from "@satyrsmc/shared/client";

const POSTER_RATIOS = [
  { value: "1:1", label: "Square (1:1)", ratio: 1 },
  { value: "1.91:1", label: "Landscape (1.91:1)", ratio: 1.91 },
  { value: "4:5", label: "Portrait (4:5)", ratio: 4 / 5 },
  { value: "9:16", label: "Reel (9:16)", ratio: 9 / 16 },
] as const;

type PosterRatioValue = (typeof POSTER_RATIOS)[number]["value"];

interface AddEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (eventId: string) => void;
  /** When on Rides page, pre-select event_type as "rides" */
  defaultEventType?: EventType;
}

export function AddEventDialog({
  open,
  onOpenChange,
  onSuccess,
  defaultEventType = EventType.Badger,
}: AddEventDialogProps) {
  const [name, setName] = useState("");
  const [eventType, setEventType] = useState<EventType>(defaultEventType);
  const [year, setYear] = useState<number | "">(new Date().getFullYear());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [showOnWebsite, setShowOnWebsite] = useState(true);
  const [membersOnly, setMembersOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  const [posterRatio, setPosterRatio] = useState<PosterRatioValue>("4:5");
  const [posterBase64, setPosterBase64] = useState<string | null>(null);
  const [cropperOpen, setCropperOpen] = useState(false);

  const createEventMutation = useCreateEvent();

  useEffect(() => {
    if (open) {
      setEventType(defaultEventType);
    }
  }, [open, defaultEventType]);

  const reset = () => {
    setName("");
    setEventType(defaultEventType);
    setYear(new Date().getFullYear());
    setStartDate("");
    setEndDate("");
    setDescription("");
    setShowOnWebsite(true);
    setMembersOnly(false);
    setPosterBase64(null);
    setPosterRatio("4:5");
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setSaving(true);
    try {
      const event = await createEventMutation.mutateAsync({
        name: trimmedName,
        event_type: eventType,
        year: year === "" ? undefined : Number(year),
        start_date: startDate.trim() || undefined,
        end_date: endDate.trim() || undefined,
        description: description.trim() || undefined,
        show_on_website: showOnWebsite,
        members_only: membersOnly,
        poster: posterBase64 ?? undefined,
      } as Record<string, unknown>);
      reset();
      onOpenChange(false);
      onSuccess(event.id);
    } finally {
      setSaving(false);
    }
  };

  const activeCropRatio = POSTER_RATIOS.find((r) => r.value === posterRatio)!;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create event</DialogTitle>
            <DialogDescription className="sr-only">
              Enter event name, type, year, date, and optional description.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Event name"
              />
            </div>
            <div className="space-y-2">
              <Label>Event type</Label>
              <Select value={eventType} onValueChange={(v) => setEventType(v as EventType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="badger">{EVENT_TYPE_LABELS.badger}</SelectItem>
                  <SelectItem value="social">{EVENT_TYPE_LABELS.social}</SelectItem>
                  <SelectItem value="pioneer_run">{EVENT_TYPE_LABELS.pioneer_run}</SelectItem>
                  <SelectItem value="rides">{EVENT_TYPE_LABELS.rides}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Year</Label>
                <Input
                  type="number"
                  value={year}
                  onChange={(e) =>
                    setYear(e.target.value === "" ? "" : parseInt(e.target.value, 10))
                  }
                  placeholder="2025"
                />
              </div>
              <div className="space-y-2">
                <Label>Start date</Label>
                <DatePicker value={startDate} onChange={setStartDate} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>End date</Label>
                <DatePicker value={endDate} onChange={setEndDate} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <EventDescriptionEditor
                value={description}
                onChange={setDescription}
                placeholder="Brief description..."
              />
            </div>
            <div className="space-y-2">
              <Label>Event poster (optional)</Label>
              <div className="flex gap-2">
                <Select
                  value={posterRatio}
                  onValueChange={(v) => {
                    setPosterRatio(v as PosterRatioValue);
                    setPosterBase64(null);
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSTER_RATIOS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant={posterBase64 ? "default" : "outline"}
                  onClick={() => setCropperOpen(true)}
                >
                  {posterBase64 ? "Change" : "Upload"}
                </Button>
                {posterBase64 && (
                  <Button type="button" variant="ghost" onClick={() => setPosterBase64(null)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showOnWebsite}
                onChange={(e) => setShowOnWebsite(e.target.checked)}
                className="size-4 rounded border-input"
              />
              <span className="text-sm font-medium">Show on website</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={membersOnly}
                onChange={(e) => setMembersOnly(e.target.checked)}
                className="size-4 rounded border-input"
              />
              <span className="text-sm font-medium">Members only</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!name.trim() || saving}>
              {saving ? "Creating..." : "Create event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageUploadCropper
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        onConfirm={(base64) => setPosterBase64(base64)}
        aspectRatio={activeCropRatio.ratio}
        title={`Event Poster — ${activeCropRatio.label}`}
      />
    </>
  );
}
